import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  isToolsDraftEmptyError,
  isToolsDraftNonEmptyError,
  useDispatchJob,
} from "@/api/runs";
import { ApiError } from "@/api/client";
import { useMemos, type MemoPickerRow } from "@/api/memos";
import {
  usePresentations,
  type PresentationPickerRow,
} from "@/api/presentations";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * `/run` — operator-driven manual dispatch. The page is gated by an
 * **Agent** select at the top; the cards below switch on the selection:
 *
 * 1. **Curator** (`db_curator`) — two cards:
 *    - **Autonomous run** — single button, no input. POSTs `{ agentType:
 *      "db_curator", context: { mode: "autonomous" } }` and navigates to
 *      `/jobs/:id` on 202.
 *    - **Targeted search** — textarea (one tool name per line or comma-
 *      separated). On submit, parses + dedupes, then POSTs `{ agentType:
 *      "db_curator", context: { mode: "targeted", toolNames: [...] } }`.
 *    Both share the `tools_draft_non_empty` 409 path: the orchestrator
 *    pre-flights the draft sheet and rejects with `{ error:
 *    "tools_draft_non_empty", pendingCount }`. We render a banner with a
 *    deep link to the review queue so the operator can clear it.
 * 2. **Validator (catch-up)** (`db_curator_validator`) — single card,
 *    single button, no input. POSTs `{ agentType: "db_curator_validator",
 *    context: {} }`. The validator ignores `context` entirely; on each
 *    dispatch it scores up to `validator_max_rows_per_run` unrated rows
 *    from `tools_draft` and stops. Re-dispatch to chew through a
 *    backlog. The 409 guard never fires here — the validator is the
 *    consumer of the draft sheet.
 * 3. **Analyst** (`analyst`) — memo picker + optional `excludedToolIds`
 *    textarea. POSTs `{ agentType: "analyst", context: { memoId,
 *    excludedToolIds? } }`. The operator recovery path when
 *    intake-validator halts the auto-chain on a `flagged` memo (issue
 *    #571); `excludedToolIds` mirrors the Match Validator retry chain
 *    parameter for advanced re-runs.
 * 4. **Match Validator** (`match-validator`) — presentation picker.
 *    POSTs `{ agentType: "match-validator", context: { memoId,
 *    presentationId } }` where both come from the picked row. Lets
 *    the operator re-validate a `draft` / `pending_review` /
 *    `finalised` / `sent` presentation; terminal-verdict statuses
 *    (`approved`, `rejected`, `expired`, `failed`) are filtered out at
 *    query time.
 * 5. **Intake Validator** (`intake-validator`) — memo picker. POSTs
 *    `{ agentType: "intake-validator", context: { memoId } }`. Re-runs
 *    the intake-validator idempotently — the outcome hook overwrites
 *    `problem_memos.validation_status` in place.
 *
 * **Single-active-run guards** for the three M2 agents: **none**. The
 * M1 curator's `tools_draft_non_empty` guard is bespoke to the Sheets
 * write path; none of the M2 agents write `tools_draft`. Concurrent
 * dispatch on the same memo relies on the agent's own idempotency:
 * the Analyst has `solution_presentations_memo_id_draft_unique` on
 * the draft write; intake-validator + match-validator overwrite the
 * relevant columns in place. Documented so a future audit doesn't
 * re-derive the answer.
 *
 * The agent allow-list is a UI-side const rather than a fetch from the
 * `agents` table, per issue #62 — minimal blast radius, explicit list
 * of who the operator can dispatch.
 */
const DISPATCHABLE_AGENTS = [
  { value: "db_curator", label: "Curator" },
  { value: "db_curator_validator", label: "Validator (catch-up)" },
  { value: "analyst", label: "Analyst" },
  { value: "match-validator", label: "Match Validator" },
  { value: "intake-validator", label: "Intake Validator" },
] as const;

type DispatchableAgent = (typeof DISPATCHABLE_AGENTS)[number]["value"];

/** Same cap as `curatorContextSchema` in `@prospect/shared`. */
const MAX_TOOL_NAMES = 50;

export function ManualRun() {
  const [agent, setAgent] = useState<DispatchableAgent>("db_curator");

  return (
    <div className="space-y-6">
      <div>
        <p className="label-mono">Manual controls</p>
        <h1 className="font-display text-4xl text-text">Run a job</h1>
        <p className="mt-2 text-sm text-text-muted">
          Dispatch any of the five configured agents. The Curator + Validator
          drive the M1 catalogue pipeline; the Analyst, Match Validator, and
          Intake Validator are the M2 memo → presentation → HITL chain and
          double as operator recovery when the auto-chain halts.
        </p>
      </div>

      <AgentSelect agent={agent} onChange={setAgent} />

      {agent === "db_curator" ? (
        <>
          <AutonomousRunCard />
          <TargetedSearchCard />
        </>
      ) : agent === "db_curator_validator" ? (
        <ValidatorRunCard />
      ) : agent === "analyst" ? (
        <AnalystManualDispatchCard />
      ) : agent === "match-validator" ? (
        <MatchValidatorManualDispatchCard />
      ) : (
        <IntakeValidatorManualDispatchCard />
      )}
    </div>
  );
}

function AgentSelect({
  agent,
  onChange,
}: {
  agent: DispatchableAgent;
  onChange: (next: DispatchableAgent) => void;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor="agent-select" className="label-mono block">
        Agent
      </label>
      <select
        id="agent-select"
        value={agent}
        onChange={(e) => onChange(e.target.value as DispatchableAgent)}
        className={cn(
          "rounded border border-border bg-bg-card px-3 py-2 text-sm font-mono text-text",
          "focus:outline-none focus:ring-2 focus:ring-accent",
        )}
      >
        {DISPATCHABLE_AGENTS.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}

function AutonomousRunCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const onClick = async (): Promise<void> => {
    setErrorBanner(null);
    try {
      const result = await dispatch.mutateAsync({
        agentType: "db_curator",
        context: { mode: "autonomous" },
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Autonomous</p>
        <CardTitle>Run autonomous Curator</CardTitle>
        <CardDescription>
          Three-mode discovery: autonomous web search, monitored sources, and stale
          record refresh. Picks candidates on its own; uses the live{" "}
          <code className="font-mono text-xs">agent_config</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={() => void onClick()} disabled={dispatch.isPending}>
          {dispatch.isPending ? "Starting…" : "Run autonomous Curator"}
        </Button>
        {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
      </CardContent>
    </Card>
  );
}

function TargetedSearchCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [text, setText] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const parsed = parseToolNames(text);
  const submittable =
    parsed.tools.length > 0 &&
    parsed.tools.length <= MAX_TOOL_NAMES &&
    !dispatch.isPending;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setValidationError(null);
    setErrorBanner(null);
    if (parsed.tools.length === 0) {
      setValidationError("Enter at least one tool name.");
      return;
    }
    if (parsed.tools.length > MAX_TOOL_NAMES) {
      setValidationError(
        `Capped at ${MAX_TOOL_NAMES} tool names per run (you supplied ${parsed.tools.length}).`,
      );
      return;
    }
    try {
      const result = await dispatch.mutateAsync({
        agentType: "db_curator",
        context: { mode: "targeted", toolNames: parsed.tools },
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Targeted</p>
        <CardTitle>Targeted tool search</CardTitle>
        <CardDescription>
          Hand the Curator an explicit list of tool names. One per line or
          comma-separated. Discovery, monitoring, and stale refresh are all
          skipped — the agent researches exactly the supplied tools, in order.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={`gh-cli, terraform\nansible`}
            disabled={dispatch.isPending}
            className={cn(
              "w-full rounded border border-border bg-bg-card px-3 py-2 text-sm font-mono text-text",
              "placeholder:text-text-muted/60",
              "focus:outline-none focus:ring-2 focus:ring-accent",
              "disabled:opacity-50",
            )}
          />
          <p className="text-xs text-text-muted">
            {parsed.tools.length === 0
              ? "Enter at least one tool name."
              : `${parsed.tools.length} unique tool name${parsed.tools.length === 1 ? "" : "s"}` +
                (parsed.dedupedCount > 0
                  ? ` · ${parsed.dedupedCount} duplicate${parsed.dedupedCount === 1 ? "" : "s"} dropped`
                  : "") +
                ` · max ${MAX_TOOL_NAMES} per run.`}
          </p>
          <Button type="submit" disabled={!submittable}>
            {dispatch.isPending ? "Starting…" : "Start targeted run"}
          </Button>
          {validationError ? (
            <p className="text-xs text-red-300">{validationError}</p>
          ) : null}
          {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

function ValidatorRunCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const onClick = async (): Promise<void> => {
    setErrorBanner(null);
    try {
      const result = await dispatch.mutateAsync({
        agentType: "db_curator_validator",
        context: {},
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Catch-up</p>
        <CardTitle>Run Validator catch-up</CardTitle>
        <CardDescription>
          Score any unrated rows currently in <code className="font-mono text-xs">tools_draft</code>.
          Caps at <code className="font-mono text-xs">validator_max_rows_per_run</code> per
          dispatch — re-run to chew through a larger backlog. Already-scored
          rows are skipped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={() => void onClick()} disabled={dispatch.isPending}>
          {dispatch.isPending ? "Starting…" : "Run Validator catch-up"}
        </Button>
        {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Analyst manual dispatch — operator recovery when the intake-validator
 * auto-chain halted on a `flagged` memo, or when the operator wants a
 * fresh analyst pass with specific tools excluded (mirrors the M2
 * Match Validator retry chain input). No single-active-run guard;
 * the DB-side `solution_presentations_memo_id_draft_unique` partial
 * index catches a concurrent duplicate draft write.
 */
function AnalystManualDispatchCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [memoId, setMemoId] = useState<string | null>(null);
  const [excludedText, setExcludedText] = useState("");
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const excludedToolIds = parseCommaOrNewlineList(excludedText);
  const submittable = memoId !== null && !dispatch.isPending;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setErrorBanner(null);
    if (memoId === null) return;
    const context: Record<string, unknown> = { memoId };
    if (excludedToolIds.length > 0) {
      context.excludedToolIds = excludedToolIds;
    }
    try {
      const result = await dispatch.mutateAsync({
        agentType: "analyst",
        context,
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Analyst</p>
        <CardTitle>Run Analyst against a memo</CardTitle>
        <CardDescription>
          Advance a memo to the Analyst — most useful when the intake-validator
          returned <code className="font-mono text-xs">flagged</code> and the
          auto-chain halted. Optionally exclude specific catalogue tool ids to
          bias the search away from prior offenders (mirrors the Match
          Validator retry-chain input).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <MemoPicker memoId={memoId} onChange={setMemoId} />
          <div className="space-y-1">
            <label
              htmlFor="analyst-excluded"
              className="label-mono block text-xs"
            >
              Excluded tool ids (optional)
            </label>
            <textarea
              id="analyst-excluded"
              value={excludedText}
              onChange={(e) => setExcludedText(e.target.value)}
              rows={3}
              placeholder="uuid-1, uuid-2\nuuid-3"
              disabled={dispatch.isPending}
              className={cn(
                "w-full rounded border border-border bg-bg-card px-3 py-2 text-sm font-mono text-text",
                "placeholder:text-text-muted/60",
                "focus:outline-none focus:ring-2 focus:ring-accent",
                "disabled:opacity-50",
              )}
            />
            {excludedToolIds.length > 0 ? (
              <p className="text-xs text-text-muted">
                {excludedToolIds.length} tool id
                {excludedToolIds.length === 1 ? "" : "s"} will be excluded.
              </p>
            ) : null}
          </div>
          <Button type="submit" disabled={!submittable}>
            {dispatch.isPending ? "Starting…" : "Dispatch Analyst"}
          </Button>
          {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Match Validator manual dispatch. Picks a presentation directly —
 * `memoId` and `presentationId` both come from the picked row. Lets
 * the operator re-run the validator on a `draft` / `pending_review`
 * / `finalised` / `sent` presentation without a curl or psql
 * intervention. No single-active-run guard; the validator's outcome
 * hook overwrites `solution_presentations.match_validation_status`
 * in place.
 */
function MatchValidatorManualDispatchCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [presentation, setPresentation] = useState<PresentationPickerRow | null>(
    null,
  );
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const submittable = presentation !== null && !dispatch.isPending;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setErrorBanner(null);
    if (presentation === null) return;
    try {
      const result = await dispatch.mutateAsync({
        agentType: "match-validator",
        context: {
          memoId: presentation.memoId,
          presentationId: presentation.id,
        },
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Match Validator</p>
        <CardTitle>Run Match Validator against a presentation</CardTitle>
        <CardDescription>
          Re-validate an existing presentation. Useful after an operator edits
          the recommendations set, or when the auto-chain wedged. The picker
          shows non-terminal presentations only — approved / rejected /
          expired / failed rows aren't re-dispatchable.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <PresentationPicker
            presentation={presentation}
            onChange={setPresentation}
          />
          <Button type="submit" disabled={!submittable}>
            {dispatch.isPending ? "Starting…" : "Dispatch Match Validator"}
          </Button>
          {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Intake Validator manual dispatch. Rerunning is idempotent — the
 * outcome hook overwrites `problem_memos.validation_status` /
 * `validation_score` / `validation_report` in place. Useful when
 * the intake-validator failed to dispatch on the initial
 * `memo_confirmed` (see `intake_validator_dispatch_failed` audit
 * event), or when an operator wants to re-score a memo after a
 * config / prompt change.
 */
function IntakeValidatorManualDispatchCard() {
  const navigate = useNavigate();
  const dispatch = useDispatchJob();
  const [memoId, setMemoId] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null);

  const submittable = memoId !== null && !dispatch.isPending;

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setErrorBanner(null);
    if (memoId === null) return;
    try {
      const result = await dispatch.mutateAsync({
        agentType: "intake-validator",
        context: { memoId },
      });
      navigate(`/jobs/${result.jobId}`);
    } catch (err) {
      setErrorBanner(toErrorBanner(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Intake Validator</p>
        <CardTitle>Run Intake Validator against a memo</CardTitle>
        <CardDescription>
          Re-score a memo. The verdict overwrites{" "}
          <code className="font-mono text-xs">problem_memos.validation_status</code>{" "}
          / <code className="font-mono text-xs">validation_score</code> /{" "}
          <code className="font-mono text-xs">validation_report</code> in place,
          so a re-run is safe and idempotent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <MemoPicker memoId={memoId} onChange={setMemoId} />
          <Button type="submit" disabled={!submittable}>
            {dispatch.isPending ? "Starting…" : "Dispatch Intake Validator"}
          </Button>
          {errorBanner ? <ErrorBannerView banner={errorBanner} /> : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Native `<select>` over the most-recent 50 memos, matching the visual
 * style of the top-level agent select. Options show
 * `{shortId} · {clientName} · {validationStatus} · {createdAt}`; the
 * value in state is the full memo UUID. Loading + error + empty states
 * are surfaced inline; the picker never blocks Submit rendering
 * itself.
 */
function MemoPicker({
  memoId,
  onChange,
}: {
  memoId: string | null;
  onChange: (next: string | null) => void;
}) {
  const memos = useMemos();

  if (memos.isLoading) {
    return <PickerHelper text="Loading memos…" />;
  }
  if (memos.isError) {
    return <PickerHelper text="Failed to load memos." tone="error" />;
  }
  const items = memos.data?.items ?? [];
  if (items.length === 0) {
    return <PickerHelper text="No memos yet." />;
  }

  return (
    <div className="space-y-1">
      <label htmlFor="memo-picker" className="label-mono block text-xs">
        Memo
      </label>
      <select
        id="memo-picker"
        value={memoId ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={cn(
          "w-full rounded border border-border bg-bg-card px-3 py-2 text-sm font-mono text-text",
          "focus:outline-none focus:ring-2 focus:ring-accent",
        )}
      >
        <option value="">— Select a memo —</option>
        {items.map((memo) => (
          <option key={memo.id} value={memo.id}>
            {formatMemoOption(memo)}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatMemoOption(memo: MemoPickerRow): string {
  const client =
    memo.clientName && memo.clientCompany
      ? `${memo.clientName} @ ${memo.clientCompany}`
      : memo.clientName ?? memo.clientCompany ?? "—";
  const created = new Date(memo.createdAt).toISOString().slice(0, 10);
  return `${memo.shortId} · ${client} · ${memo.validationStatus} · ${created}`;
}

/**
 * Native `<select>` over the most-recent 50 non-terminal presentations.
 * The value in state is the full picked row (not just the id) because
 * the caller needs both `memoId` and `presentationId` — carrying the
 * whole row is cheaper than a second lookup on submit.
 */
function PresentationPicker({
  presentation,
  onChange,
}: {
  presentation: PresentationPickerRow | null;
  onChange: (next: PresentationPickerRow | null) => void;
}) {
  const presentations = usePresentations();

  if (presentations.isLoading) {
    return <PickerHelper text="Loading presentations…" />;
  }
  if (presentations.isError) {
    return <PickerHelper text="Failed to load presentations." tone="error" />;
  }
  const items = presentations.data?.items ?? [];
  if (items.length === 0) {
    return <PickerHelper text="No non-terminal presentations." />;
  }

  return (
    <div className="space-y-1">
      <label
        htmlFor="presentation-picker"
        className="label-mono block text-xs"
      >
        Presentation
      </label>
      <select
        id="presentation-picker"
        value={presentation?.id ?? ""}
        onChange={(e) => {
          const next = items.find((p) => p.id === e.target.value) ?? null;
          onChange(next);
        }}
        className={cn(
          "w-full rounded border border-border bg-bg-card px-3 py-2 text-sm font-mono text-text",
          "focus:outline-none focus:ring-2 focus:ring-accent",
        )}
      >
        <option value="">— Select a presentation —</option>
        {items.map((row) => (
          <option key={row.id} value={row.id}>
            {formatPresentationOption(row)}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatPresentationOption(row: PresentationPickerRow): string {
  const client =
    row.clientName && row.clientCompany
      ? `${row.clientName} @ ${row.clientCompany}`
      : row.clientName ?? row.clientCompany ?? "—";
  const created = new Date(row.createdAt).toISOString().slice(0, 10);
  return `${row.shortId} · memo ${row.memoShortId} · ${client} · ${row.status}/${row.matchValidationStatus} · ${created}`;
}

function PickerHelper({
  text,
  tone,
}: {
  text: string;
  tone?: "error";
}) {
  return (
    <p
      className={cn(
        "text-xs",
        tone === "error" ? "text-red-300" : "text-text-muted",
      )}
    >
      {text}
    </p>
  );
}

interface ErrorBanner {
  kind: "tools_draft_non_empty" | "tools_draft_empty" | "generic";
  message: string;
  pendingCount?: number;
  /**
   * Unrated row count on the 409 `tools_draft_non_empty` body — see
   * issue #641. Non-zero → the operator's next step is the Validator
   * (catch-up); the banner deep-links to `/run` and swaps the copy
   * accordingly. Zero (or absent from the response) → drafts are
   * fully scored and awaiting HITL; the banner keeps the legacy
   * review-queue link.
   */
  unratedCount?: number;
}

function ErrorBannerView({ banner }: { banner: ErrorBanner }) {
  if (banner.kind === "tools_draft_non_empty") {
    // Unrated leftovers (targeted-mode curator that skipped the
    // validator, or a partial catch-up) route to the Validator
    // catch-up on the same `/run` page; fully-scored leftovers keep
    // the historical review-queue link. See issue #641.
    const hasUnrated =
      banner.unratedCount !== undefined && banner.unratedCount > 0;
    return (
      <div
        role="alert"
        className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 space-y-1"
      >
        <p className="text-sm text-red-200">
          A previous draft batch is still pending review.
          {banner.pendingCount !== undefined
            ? ` ${banner.pendingCount} row${banner.pendingCount === 1 ? "" : "s"} in tools_draft`
            : ""}
          {hasUnrated
            ? ` (${banner.unratedCount} unrated).`
            : banner.pendingCount !== undefined
              ? "."
              : ""}
        </p>
        {hasUnrated ? (
          <p className="text-xs text-red-300/80">
            Send them through the Validator (catch-up) first, then
            approve or reject in the review queue.{" "}
            <Link to="/run" className="underline">
              Go to Validator (catch-up) →
            </Link>
          </p>
        ) : (
          <p className="text-xs text-red-300/80">
            Clear it before starting another run.{" "}
            <Link to="/review" className="underline">
              Go to review queue →
            </Link>
          </p>
        )}
      </div>
    );
  }
  if (banner.kind === "tools_draft_empty") {
    return (
      <div
        role="alert"
        className="rounded border border-amber-500/30 bg-amber-500/10 px-4 py-3 space-y-1"
      >
        <p className="text-sm text-amber-200">
          No unrated rows in <code className="font-mono text-xs">tools_draft</code> —
          nothing for the validator to score.
        </p>
        <p className="text-xs text-amber-300/80">
          Run the Curator to write new draft rows, then re-dispatch the
          validator catch-up.
        </p>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3"
    >
      <p className="text-sm text-red-200">{banner.message}</p>
    </div>
  );
}

function toErrorBanner(err: unknown): ErrorBanner {
  if (isToolsDraftNonEmptyError(err)) {
    const body = (err as ApiError).body as {
      pendingCount?: unknown;
      unratedCount?: unknown;
    };
    const pendingCount =
      typeof body.pendingCount === "number" ? body.pendingCount : undefined;
    const unratedCount =
      typeof body.unratedCount === "number" ? body.unratedCount : undefined;
    return {
      kind: "tools_draft_non_empty",
      message: "tools_draft is not empty",
      pendingCount,
      unratedCount,
    };
  }
  if (isToolsDraftEmptyError(err)) {
    return {
      kind: "tools_draft_empty",
      message: "tools_draft has no unrated rows",
    };
  }
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    const code = body && typeof body.error === "string" ? body.error : null;
    return {
      kind: "generic",
      message:
        code !== null
          ? `Failed to start run (${code}, status ${err.status}).`
          : `Failed to start run (status ${err.status}).`,
    };
  }
  return {
    kind: "generic",
    message: err instanceof Error ? err.message : "Failed to start run.",
  };
}

/**
 * Parse a textarea blob into a list of tool names. Splits on `\n` and
 * `,`, trims, drops empties, dedupes (case-insensitive on first
 * occurrence). Returns the parsed list plus a count of duplicates we
 * dropped so the helper text can surface "N duplicates removed".
 */
export function parseToolNames(input: string): {
  tools: string[];
  dedupedCount: number;
} {
  const parts = input
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const tools: string[] = [];
  let dedupedCount = 0;
  for (const part of parts) {
    const key = part.toLowerCase();
    if (seen.has(key)) {
      dedupedCount += 1;
      continue;
    }
    seen.add(key);
    tools.push(part);
  }
  return { tools, dedupedCount };
}

/**
 * Simpler sibling of {@link parseToolNames} for the Analyst's
 * `excludedToolIds` textarea. The input is a list of catalogue tool
 * UUIDs; catalogue rows are stored with lowercase UUIDs, and the
 * Analyst does a plain-string `Set` lookup at
 * `services/analyst/src/run.ts:234`, so an operator pasting an
 * upper-cased id (or a mixed-case copy-paste from another surface)
 * would silently miss its target row. Lowercase-normalise here so
 * the string compare downstream is case-safe. Splits on `\n` and
 * `,`, trims, lowercases, drops empties, then dedupes.
 */
export function parseCommaOrNewlineList(input: string): string[] {
  const parts = input
    .split(/[,\n]/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
  return [...new Set(parts)];
}
